import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';

import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../app.settings';

import { ProcessingComponent } from '../../../components';

import { successMsg } from '../../../common/messages';
import { notifyAndTrackError } from '../../../common/error';
import { AppInsightsService } from '../../../services/app-insights.service';
import { IPermission, IRole } from '../../../services/insecta';
import { PermissionAdminService } from '../../../services/insecta/admin/security-admin.service';
import { IRolePermission, IRolePermissionUpdateRequest } from '../../../services/insecta/insecta.models';
import { MSGraphService } from '../../../services/msgraph.service';

@Component({
  selector: 'app-permissions-admin',
  standalone: true,
  imports: [ButtonModule, CardModule, CheckboxModule, FormsModule, SelectModule, TableModule, ProcessingComponent],

  templateUrl: './security-admin.component.html'
})
export class SecurityAdminComponent implements OnInit, OnDestroy {
  roles: IRole[] = [];
  selectedRole: IRole | undefined;
  permissions: IPermission[] = [];
  rolePermissions: IRolePermission[] = [];
  selectedPermissions: IPermission[] = [];

  dialogConfirmSub: ((leave: boolean) => void) | null = null;

  pageVars: any = {
    currentUser: '',
    isReady: false,
    saveEnabled: false,
    pageId: 'Admin:Permissions'
  };

  constructor(
    private apiService: PermissionAdminService,
    private appInsights: AppInsightsService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:Permissions`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/permissions`);
  }

  ngOnDestroy() {
    this.pageVars = {
      isReady: false
    };
  }

  ngOnInit() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);

    const request = {
      profile: this.graphService.getMyProfile$(),
      roles: this.apiService.getRoles(),
      permissions: this.apiService.getPermissions()
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.roles = response.roles;
        this.roles.sort((a, b) => a.name.localeCompare(b.name));
        this.permissions = response.permissions;
        this.pageVars.currentUser = response.profile.userPrincipalName;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'Init:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  updateAppUserTable() {
    this.pageVars.isReady = false;
    this.appInsights.trackEvent(`${this.pageVars.pageId}:UpdateAppUserTable`);

    firstValueFrom(this.apiService.updateAppUserTable())
      .then(() => {
        successMsg(this.msgService, 'Users Updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'UpdateAppUserTable:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  roleSelected() {
    this.selectedPermissions = [];
    this.pageVars.isReady = false;
    firstValueFrom(this.apiService.getRolePermissionsByRoleId(this.selectedRole!.id))
      .then((response) => {
        this.rolePermissions = response;
        this.selectedPermissions = this.rolePermissions.map((rp) => rp.permission);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'RoleSelected:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.saveEnabled = true;
      });
  }

  savePermissions() {
    this.pageVars.isReady = false;

    const updatedPermissions: IRolePermissionUpdateRequest = {
      roleId: this.selectedRole!.id,
      permissions: this.selectedPermissions.map((x) => x.id),
      actionBy: this.pageVars.currentUser
    };

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Save`, { UpdatedPermissions: updatedPermissions });

    firstValueFrom(this.apiService.updateRolePermissions(updatedPermissions))
      .then(() => {
        successMsg(this.msgService, 'Permissions Updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'SavePermissions:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }
}
